class ProductManager {
    constructor() {
        if (!window.ProductState) {
            console.error('ProductState not found. Make sure product-data-layer is loaded first.');
            return;
        }

        this.data = window.ProductState;
        this.dom = this.cacheDom();
        this.constants = this.initConstants();
        this.prevVariantId = this.getUrlVariantId() || this.data.defaultVariantId;
        this.modalSwiperInstance = null;
        this.isUpdating = false;
        this.hasUserInteracted = false;

        this.init();
    }

    isHiddenOption(value) {
        if (!value) return false;
        return value.toLowerCase().includes('hidden');
    }

    extractCustomValue(optionValue) {
        if (!optionValue || !optionValue.includes('*')) {
            return optionValue ? optionValue.trim() : '';
        }

        const parts = optionValue.split('*');
        // Handle *value* format (value between asterisks)
        if (parts.length > 2 && parts[1]) {
            return parts[1].trim();
        }
        // Handle value*suffix format
        if (parts.length > 1 && parts[1]) {
            return parts[1].trim();
        }
        // Fallback to original value
        return optionValue.trim();
    }

    shouldShowSwatchContainer(swatchProperty, availableOptions) {
        const hasNonHiddenOptions = Array.from(availableOptions.keys()).some(
            (optionValue) => !this.isHiddenOption(optionValue),
        );

        return hasNonHiddenOptions;
    }

    cacheDom() {
        return {
            productDetails: document.querySelector('.product-details-page'),
            customOptions: document.querySelector('.swatch.custom-option'),
            specsTable: document.querySelector('#product-specs-table tbody'),
            productMainImages: document.querySelector('.product-grid-images'),
            productMainImagesMobile: document.querySelector('.product-grid-images-mobile'),
            imageModal: document.getElementById('image-modal'),
            modalContainer: document.querySelector('.product-grid-images-modal'),
            modalImagesContainer: document.querySelector('.product-grid-images-modal-body-content-image'),
            modalSwiper: document.querySelector('.image-preview-container swiper-container'),
            modalThumbs: document.querySelector('.image-thumbs-wrapper.image-thumbs-wrapper-product-images'),
            swatchElements: () =>
                document.querySelectorAll('#swatch-product-template .swatch:not(.custom-option) input[type="radio"]'),
            selectedVariants: () => document.querySelectorAll('.swatch-element input:checked'),
            variantProperties: () => document.querySelectorAll('.swatch:not(.custom-option) .variant-property'),
            productForm: () => document.querySelector('form[action*="/cart/add"]'),
            singleOptionSelectors: () => document.querySelectorAll('.single-option-selector'),
        };
    }

    initConstants() {
        return {
            EXCLUDED_SPECS_KEYS: new Set([
                'short_description',
                'features',
                'size',
                'warranty_certifications',
                'video',
                'documentation',
                'product_id',
                'show_variants_separated',
                'product_features',
                'installation_manual',
                'specifications',
                'product_images',
                'product_subtitle',
                'custom_variant_option',
            ]),

            SPECS_NAME_MAP: new Map([
                ['connection_size_in', 'Connection Size (in.)'],
                ['product_height_in', 'Product Height (in.)'],
                ['product_depth_in', 'Product Depth (in.)'],
                ['product_width_in', 'Product Width (in.)'],
                ['product_weight_lb', 'Product Weight (lb.)'],
                ['spout_height_in', 'Spout Height (in.)'],
                ['spout_reach_in', 'Spout Reach (in.)'],
                ['water_flow_rate_gpm', 'Water Flow Rate (GPM)'],
                ['country_of_origin', 'Country of Origin'],
                ['ada_compliant', 'ADA Compliant'],
                ['commercial_residential', 'Commercial / Residential'],
                ['number_of_faucet_handles', 'Number of Faucet Handles'],
                ['number_of_handles', 'Number of Handles'],
                ['bowl_1_left_to_right_length_in', 'Left to Right Length (in.)'],
                ['bowl_1_front_to_back_width_in', 'Front to Back Width (in.)'],
                ['bowl_1_top_to_bottom_depth_in', 'Top to Bottom Depth (in.)'],
                ['door_glass_thickness_in', 'Door Glass Thickness (in.)'],
                ['door_height_in', 'Door Height (in.)'],
                ['door_opens', 'Door Configuration'],
                ['door_width_in', 'Door Width (in.)'],
                ['max_installed_width_in', 'Max Installed Width (in.)'],
                ['min_installed_width_in', 'Min Installed Width (in.)'],
                ['number_of_pieces', 'Number of Pieces'],
                ['walk_through_opening_height_in', 'Walk-Through Opening Height (in.)'],
                ['walk_through_opening_width_min_in', 'Walk-Through Opening Width Min (in.)'],
                ['walk_through_opening_width_max_in', 'Walk-Through Opening Width Max (in.)'],
                ['number_of_jets', 'Number of Jets'],
                ['cut_out_depth_in', 'Cut Out Depth (in.)'],
                ['inner_bowl_side_to_side_width_in', 'Inner Bowl Side to Side Width (in.)'],
                ['extended_hose_length_in', 'Extended Hose Length (in.)'],
            ]),
        };
    }

    init() {
        const urlVariantId = this.getUrlVariantId();
        const hasUrlVariant = !!urlVariantId;

        if (hasUrlVariant) {
            this.syncSelectorsFromVariantId(urlVariantId);
        } else {
            const firstAvailableVariant = this.data.variants.find((v) => v.available) || this.data.variants[0];
            if (firstAvailableVariant) {
                this.syncSelectorsFromVariantId(firstAvailableVariant.id);

                // Set the URL with the variant ID to ensure proper loading
                const url = new URL(window.location);
                url.searchParams.set('variant', firstAvailableVariant.id);
                window.history.replaceState({}, '', url);
            }
        }

        this.hideHiddenOptionElements();
        this.rerenderOptions();
        this.initSwatchVariantListeners();
        this.updateCustomOption();
        this.setupNavigationListener();
        this.setupModalListeners();

        this.initDropdownListeners();

        this.updateVariantElements();

        this.updateAllHeaderValues();

        // Hide skeleton loading and show swatches
        this.hideSkeletonLoading();

        // Trigger initial variant change to load data
        requestAnimationFrame(() => {
            this.triggerVariantChange();
        });
    }

    hideSkeletonLoading() {
        const skeleton = document.getElementById('swatch-skeleton');
        const swatchVariant = document.getElementById('swatch-product-template');

        if (skeleton) {
            skeleton.classList.add('hidden');
        }

        if (swatchVariant) {
            swatchVariant.classList.remove('swatch-loading');
            swatchVariant.classList.add('swatch-loaded');
        }
    }

    hideHiddenOptionElements() {
        const variantProperties = this.dom.variantProperties();

        variantProperties.forEach((property) => {
            const swatchContainer = property.closest('.swatch');

            if (swatchContainer && swatchContainer.classList.contains('custom-option')) {
                return;
            }

            const swatchElements = property.querySelectorAll('.swatch-element');

            swatchElements.forEach((el) => {
                const input = el.querySelector('input');
                const value = el.dataset.value || input?.value;

                if (value && this.isHiddenOption(value)) {
                    const swatchContainer = el.closest('.swatch');
                    if (swatchContainer) {
                        swatchContainer.style.display = 'none';
                        swatchContainer.classList.add('hidden-option');
                    }
                    if (input) {
                        input.disabled = true;
                    }
                }
            });
        });

        const selectors = this.dom.singleOptionSelectors();
        selectors.forEach((selector) => {
            if (selector.tagName === 'SELECT') {
                Array.from(selector.options).forEach((option) => {
                    if (option.value && this.isHiddenOption(option.value)) {
                        option.style.display = 'none';
                        option.hidden = true;
                        option.disabled = true;
                    }
                });
            }
        });
    }

    initDropdownListeners() {
        const selectors = this.dom.singleOptionSelectors();
        selectors.forEach((selector, index) => {
            if (selector.tagName === 'SELECT') {
                selector.addEventListener('change', (e) => this.handleDropdownChange(e, index));
            }
        });
    }

    handleDropdownChange(event, optionIndex) {
        if (this.isUpdating) {
            event.preventDefault();
            return;
        }

        const selector = event.target;
        const selectedValue = selector.value;
        const selectedOption = selector.options[selector.selectedIndex];

        if (selectedOption && selectedOption.disabled) {
            event.preventDefault();
            return;
        }

        if (selectedOption) {
            this.updateCustomDropdownDisplay(selector, selectedOption.text);
        }

        this.syncSwatchFromDropdown(optionIndex, selectedValue);
        this.clearSubsequentOptions(optionIndex);
        this.updateVariantElements(optionIndex);
        this.updateCustomOption();

        requestAnimationFrame(() => {
            this.triggerVariantChange();
        });
    }

    syncSwatchFromDropdown(optionIndex, value) {
        const variantProperties = this.dom.variantProperties();
        if (!variantProperties[optionIndex]) return;

        const swatchElements = variantProperties[optionIndex].querySelectorAll('.swatch-element');
        swatchElements.forEach((el) => {
            const input = el.querySelector('input[type="radio"]');
            if (input && this.optionValuesMatch(input.value, value)) {
                input.checked = true;
                el.classList.add('selected');
            } else {
                if (input) {
                    input.checked = false;
                }
                el.classList.remove('selected');
            }
        });

        const dropdownItems = variantProperties[optionIndex].querySelectorAll('.option-item');
        dropdownItems.forEach((el) => {
            const input = el.querySelector('input[type="radio"]');
            if (input && this.optionValuesMatch(input.value, value)) {
                input.checked = true;
                el.classList.add('selected');

                const dropdown = el.closest('.custom-dropdown');
                if (dropdown) {
                    const label = input.nextElementSibling;
                    const optionText = label?.querySelector('.option-text')?.textContent || value.split('*')[0].trim();
                    const selectedOption = dropdown.querySelector('.selected-option');
                    if (selectedOption) {
                        selectedOption.textContent = optionText;
                    }
                }
            } else {
                if (input) {
                    input.checked = false;
                }
                el.classList.remove('selected');
            }
        });
    }

    syncSelectorsFromVariantId(variantId) {
        const variant = this.data.variants.find((v) => v.id === parseInt(variantId));
        if (!variant) return;

        const selectors = this.dom.singleOptionSelectors();
        const variantProperties = this.dom.variantProperties();

        variant.options.forEach((optionValue, index) => {
            if (selectors[index]) {
                selectors[index].value = optionValue;

                if (selectors[index].tagName === 'SELECT') {
                    const option = Array.from(selectors[index].options).find((opt) =>
                        this.optionValuesMatch(opt.value, optionValue),
                    );
                    if (option) {
                        selectors[index].value = option.value;
                    }
                }
            }

            if (variantProperties[index]) {
                const swatchElements = variantProperties[index].querySelectorAll('.swatch-element');
                swatchElements.forEach((el) => {
                    const input = el.querySelector('input[type="radio"]');
                    const elValue = el.dataset.value || input?.value;

                    const isMatch = elValue && this.optionValuesMatch(elValue, optionValue);

                    if (isMatch) {
                        if (input) {
                            input.checked = true;
                            el.classList.add('selected');
                        }
                    } else {
                        if (input) {
                            input.checked = false;
                            el.classList.remove('selected');
                        }
                    }
                });

                const dropdown = variantProperties[index].closest('.swatch')?.querySelector('.custom-dropdown');
                if (dropdown) {
                    const dropdownOptions = dropdown.querySelectorAll('.option-item input[type="radio"]');
                    dropdownOptions.forEach((input) => {
                        const isMatch = this.optionValuesMatch(input.value, optionValue);

                        if (isMatch) {
                            input.checked = true;

                            const label = input.nextElementSibling;
                            const optionText =
                                label?.querySelector('.option-text')?.textContent || optionValue.split('*')[0].trim();
                            const selectedOption = dropdown.querySelector('.selected-option');

                            if (selectedOption) {
                                selectedOption.textContent = optionText;
                            }
                        } else {
                            input.checked = false;
                        }
                    });
                }
            }
        });
    }

    setupNavigationListener() {
        if (!window.navigationListenerAdded && window.navigation) {
            window.navigation.addEventListener('navigate', () => this.handleNavigationChange());
            window.navigationListenerAdded = true;
        }
    }

    setupModalListeners() {
        const closeButton = document.querySelector('.product-grid-images-modal-close');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                if (this.dom.modalContainer) {
                    this.dom.modalContainer.style.display = 'none';
                    document.body.style.overflow = '';
                }
            });
        }

        if (this.dom.modalContainer) {
            this.dom.modalContainer.addEventListener('click', (e) => {
                if (e.target === this.dom.modalContainer) {
                    this.dom.modalContainer.style.display = 'none';
                    document.body.style.overflow = '';
                }
            });
        }
    }

    getUrlParams() {
        return new URLSearchParams(window.location.search);
    }

    getUrlVariantId() {
        return this.getUrlParams().get('variant');
    }

    getCurrentVariant() {
        const variantId = this.getUrlVariantId() || this.data.variants[0].id;
        return this.data.variants.find((variant) => variant.id === parseInt(variantId));
    }

    findVariantByOptions() {
        const selectedOptions = [];
        const selectors = this.dom.singleOptionSelectors();
        const customOptionsVisible = this.dom.customOptions && this.dom.customOptions.style.display !== 'none';
        const customOptionInput = customOptionsVisible
            ? document.querySelector('.swatch.custom-option input:checked')
            : null;

        selectors.forEach((selector, index) => {
            let optionValue = selector.value;

            // If selector is empty, try to get value from checked radio input
            if (!optionValue) {
                const checkedInput = document.querySelector(
                    `.swatch:not(.custom-option)[data-option-index="${index}"] input:checked`,
                );
                if (checkedInput) {
                    optionValue = checkedInput.value;
                }
            }

            if (optionValue) {
                // Special handling for option 1 (glass) when custom options are visible
                if (index === 1 && customOptionInput) {
                    // Check if the main option has data-full-value set
                    const mainInput = document.querySelector(
                        `.swatch:not(.custom-option)[data-option-index="${index}"] input:checked`,
                    );
                    const fullValue = mainInput?.getAttribute('data-full-value');

                    // Use data-full-value if available (e.g., "Privacy *Right Installation*")
                    // Otherwise use the custom option value
                    optionValue = fullValue || customOptionInput.value;
                }

                selectedOptions.push(optionValue);
            }
        });

        if (selectedOptions.length === 0) {
            return null;
        }

        const foundVariant = this.data.variants.find((variant) => {
            for (let i = 0; i < selectedOptions.length; i++) {
                const selectedValue = selectedOptions[i];
                const variantValue = variant.options[i];

                // Normalize both values for comparison
                const normalizeValue = (val) => {
                    if (!val) return '';
                    return val.replace(/\s+/g, ' ').trim();
                };

                const normalizedSelected = normalizeValue(selectedValue);
                const normalizedVariant = normalizeValue(variantValue);

                if (normalizedVariant !== normalizedSelected) {
                    return false;
                }
            }
            return true;
        });

        return foundVariant;
    }

    triggerVariantChange() {
        if (this.isUpdating) {
            setTimeout(() => this.triggerVariantChange(), 20);
            return;
        }
        const variant = this.findVariantByOptions();

        if (!variant) {
            return;
        }

        const currentVariantId = this.getUrlVariantId();
        const needsUrlUpdate = !currentVariantId || parseInt(currentVariantId) !== variant.id;

        if (needsUrlUpdate) {
            const url = new URL(window.location);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url);
        }

        const form = this.dom.productForm();
        if (form) {
            const variantIdInput = form.querySelector('select[name="id"], input[name="id"]');
            if (variantIdInput) {
                variantIdInput.value = variant.id;
            }
        }

        if (window.sp && window.sp.productPage) {
            const productData = this.dom.productDetails?.dataset?.jsonProduct;
            if (productData) {
                const product = JSON.parse(productData);
                window.sp.productPage({
                    money_format: product.money_format || '${{amount}}',
                    variant: variant,
                });
            }
        }

        this.formatImageData();
        this.loadVariantData(variant.id);
    }

    deduplicateCustomOptions() {
        if (!this.dom.customOptions) return;

        const customOptionElements = this.dom.customOptions.querySelectorAll('.swatch-element');

        const seenValues = new Set();

        customOptionElements.forEach((el, index) => {
            const input = el.querySelector('input');
            if (!input) return;

            const customValue = this.extractCustomValue(input.value);

            if (seenValues.has(customValue)) {
                el.classList.add('duplicate-option');
            } else {
                seenValues.add(customValue);
                el.classList.remove('duplicate-option');
            }
        });
    }

    updateCustomOption() {
        if (!this.dom.customOptions) {
            return;
        }

        const selectedVariants = Array.from(this.dom.selectedVariants());
        const dropdownInputs = document.querySelectorAll('.custom-dropdown .option-item input:checked');
        const allSelectedInputs = [...selectedVariants, ...dropdownInputs].slice(0, 3);

        // Get currently selected option values
        const selectedOptions = allSelectedInputs.map((input) => input.value); // Check if variants exist with asterisks for the EXACT current selection
        // This includes checking the size (option 3) to ensure custom options only show when needed
        const matchingVariants = [];
        const hasCustomVariants = this.data.variants.some((variant) => {
            // Check if this variant matches ALL the selected options exactly
            let matches = true;

            // Must match all selected options (including size)
            for (let i = 0; i < selectedOptions.length; i++) {
                if (selectedOptions[i] && variant.options[i]) {
                    const variantOption = variant.options[i];
                    const selectedOption = selectedOptions[i];

                    // Compare base values (without asterisk content)
                    if (!this.optionValuesMatch(variantOption, selectedOption)) {
                        matches = false;
                        break;
                    }
                } else if (selectedOptions[i] && !variant.options[i]) {
                    // If we have a selection but variant doesn't have this option, no match
                    matches = false;
                    break;
                }
            }

            // If it matches ALL selected options AND has an asterisk in option 1 (glass), return true
            return matches && variant.options[1] && variant.options[1].includes('*');
        });

        this.dom.customOptions.style.display = hasCustomVariants ? 'flex' : 'none';

        if (hasCustomVariants) {
            this.deduplicateCustomOptions();

            // Check if there's already a custom option checked
            const alreadyCheckedCustomInput = this.dom.customOptions.querySelector('input:checked');

            if (!alreadyCheckedCustomInput) {
                // Find the first variant with asterisk that matches ALL selected options (including size)
                const matchingCustomVariant = this.data.variants.find((variant) => {
                    let matches = true;

                    // Must match ALL selected options exactly
                    for (let i = 0; i < selectedOptions.length; i++) {
                        if (selectedOptions[i] && variant.options[i]) {
                            if (!this.optionValuesMatch(variant.options[i], selectedOptions[i])) {
                                matches = false;
                                break;
                            }
                        } else if (selectedOptions[i] && !variant.options[i]) {
                            // If we have a selection but variant doesn't have this option, no match
                            matches = false;
                            break;
                        }
                    }

                    return matches && variant.options[1] && variant.options[1].includes('*');
                });

                console.log(
                    '🔍 updateCustomOption: Matching custom variant for auto-select:',
                    matchingCustomVariant?.title,
                );

                // Check if there's a variant WITHOUT custom option (no asterisk) for current selection
                const hasNonCustomVariant = this.data.variants.some((variant) => {
                    let matches = true;
                    for (let i = 0; i < selectedOptions.length; i++) {
                        if (selectedOptions[i] && variant.options[i]) {
                            if (!this.optionValuesMatch(variant.options[i], selectedOptions[i])) {
                                matches = false;
                                break;
                            }
                        }
                    }
                    return matches && (!variant.options[1] || !variant.options[1].includes('*'));
                });

                // Auto-select if:
                // 1. On initial load (!hasUserInteracted), OR
                // 2. Custom option is REQUIRED (no variant exists without asterisk)
                const shouldAutoSelect = matchingCustomVariant && (!this.hasUserInteracted || !hasNonCustomVariant);

                if (shouldAutoSelect) {
                    const customOptionValue = this.extractCustomValue(matchingCustomVariant.options[1]);

                    // Find and select the matching custom option
                    const customInputs = Array.from(this.dom.customOptions.querySelectorAll('.swatch-element input'));

                    const customOptionInput = customInputs.find((input) => {
                        const inputValue = this.extractCustomValue(input.value);
                        return inputValue === customOptionValue;
                    });

                    if (customOptionInput) {
                        const swatchElements = this.dom.customOptions.querySelectorAll('.swatch-element');
                        swatchElements.forEach((el) => el.classList.remove('selected'));

                        customOptionInput.checked = true;
                        const parentElement = customOptionInput.closest('.swatch-element');
                        parentElement?.classList.add('selected');

                        // Update the data value display
                        const dataValue = this.dom.customOptions?.querySelector('.data-value');
                        if (dataValue) {
                            dataValue.textContent = customOptionValue;
                        }
                    }
                }
            }

            // Update soldout/available classes for custom options
            this.updateCustomOptionAvailability(allSelectedInputs);
        }
    }

    updateCustomOptionAvailability(selectedInputs) {
        // Get all custom option elements
        const customElements = Array.from(
            this.dom.customOptions.querySelectorAll('.swatch-element:not(.duplicate-option)'),
        );

        // Get currently selected options (Color, Glass, Size)
        const selectedOptions = selectedInputs.map((input) => {
            const fullValue = input.getAttribute('data-full-value');
            return fullValue || input.value;
        });

        customElements.forEach((el) => {
            const input = el.querySelector('input');
            if (!input) return;

            const customValue = input.value; // e.g., "Fluted *Left Installation*"

            // Find variants that match the selected options + this custom option
            const matchingVariants = this.data.variants.filter((variant) => {
                const variantOptions = variant.options;
                let matches = true;

                // Match option 0 (Color)
                if (selectedOptions[0]) {
                    const option0Match = this.optionValuesMatch(variantOptions[0], selectedOptions[0]);
                    if (!option0Match) matches = false;
                }

                // Match option 1 (Glass/Pattern - must match exactly with custom)
                if (variantOptions[1]) {
                    const normalized1 = variantOptions[1].toLowerCase().replace(/\s+/g, ' ').trim();
                    const normalized2 = customValue.toLowerCase().replace(/\s+/g, ' ').trim();
                    if (normalized1 !== normalized2) matches = false;
                }

                // Match option 2 (Size)
                if (selectedOptions[2]) {
                    const option2Match = this.optionValuesMatch(variantOptions[2], selectedOptions[2]);
                    if (!option2Match) matches = false;
                }

                return matches;
            });

            const hasAvailableStock = matchingVariants.some((v) => v.available);

            el.classList.toggle('soldout', !hasAvailableStock);
            el.classList.toggle('available', hasAvailableStock);
        });
    }

    updateAllHeaderValues() {
        const variantProperties = this.dom.variantProperties();

        variantProperties.forEach((property, index) => {
            const swatchContainer = property.closest('.swatch');
            if (!swatchContainer) return;

            if (swatchContainer.classList.contains('custom-option')) {
                return;
            }

            const dataValue = swatchContainer.querySelector('.header .data-value');
            if (!dataValue) return;

            const checkedInput = property.querySelector('input:checked');
            if (checkedInput) {
                let displayValue;
                const optionValue = checkedInput.value;

                if (optionValue.includes('*')) {
                    const parts = optionValue.split('*');
                    displayValue = parts[0].trim();
                } else {
                    displayValue = optionValue.trim();
                }

                dataValue.textContent = displayValue;
            }
        });

        if (this.dom.customOptions && this.dom.customOptions.style.display !== 'none') {
            const customDataValue = this.dom.customOptions.querySelector('.data-value');
            const checkedCustomInput = this.dom.customOptions.querySelector('input:checked');

            if (customDataValue && checkedCustomInput) {
                const customValue = this.extractCustomValue(checkedCustomInput.value);
                customDataValue.textContent = customValue;
            }
        }
    }

    deduplicateCustomOptions() {
        if (!this.dom.customOptions) return;

        const customOptionElements = this.dom.customOptions.querySelectorAll('.swatch-element');

        customOptionElements.forEach((el, index) => {
            const input = el.querySelector('input');
        });

        const seenValues = new Set();

        customOptionElements.forEach((el, index) => {
            const input = el.querySelector('input');
            if (!input) return;

            const fullValue = input.value;
            const customValue = this.extractCustomValue(fullValue);

            if (seenValues.has(customValue)) {
                el.style.display = 'none';
                el.classList.add('duplicate-option');
            } else {
                seenValues.add(customValue);
                el.style.display = '';
                el.classList.remove('duplicate-option');
            }
        });
    }

    initSwatchVariantListeners() {
        this.dom.swatchElements().forEach((swatch) => {
            if (
                !swatch.parentElement.classList.contains('disabled') &&
                !swatch.parentElement.classList.contains('hidden-option')
            ) {
                swatch.addEventListener('change', (e) => this.handleSwatchChange(e));
            }
        });

        this.initCustomOptionListeners();

        const showerPanInputs = document.querySelectorAll('.custom-dropdown .option-item input[type="radio"]');
        showerPanInputs.forEach((input) => {
            input.removeEventListener('change', this.handleShowerPanChange);

            input.addEventListener('change', (e) => {
                if (e.target.checked) {
                    this.handleShowerPanChange(e);
                }
            });
        });
    }

    initCustomOptionListeners() {
        const customOptionInputs = document.querySelectorAll('.swatch.custom-option input[type="radio"]');

        customOptionInputs.forEach((input) => {
            input.removeEventListener('change', this._customOptionChangeHandler);

            if (!this._customOptionChangeHandler) {
                this._customOptionChangeHandler = (e) => {
                    const swatchElement = e.target.closest('.swatch-element');

                    if (swatchElement?.classList.contains('duplicate-option')) {
                        return;
                    }

                    const customOption = e.target.closest('.swatch.custom-option');
                    if (customOption) {
                        this.hasUserInteracted = true; // Mark that user has interacted
                        customOption.querySelectorAll('.swatch-element:not(.duplicate-option)').forEach((el) => {
                            el.classList.remove('selected');
                        });
                        swatchElement?.classList.add('selected');
                    }

                    this.handleCustomOptionSelection(e.target.value);
                };
            }

            input.addEventListener('change', this._customOptionChangeHandler);
        });
    }

    handleShowerPanChange(event) {
        if (this.isUpdating) {
            event.preventDefault();
            return;
        }

        const input = event.target;
        const dropdown = input.closest('.custom-dropdown');
        const swatchContainer = input.closest('.swatch');
        const optionIndex = parseInt(swatchContainer.getAttribute('data-option-index'));
        const optionValue = input.value;
        const form = input.closest('form');

        this.hasUserInteracted = true; // Mark that user has interacted
        input.checked = true;

        const selectedOption = dropdown.querySelector('.selected-option');
        const label = input.nextElementSibling;
        const optionText = label.querySelector('.option-text')?.textContent || optionValue.split('*')[0].trim();

        if (selectedOption) {
            selectedOption.textContent = optionText;
        }

        dropdown.classList.remove('active');

        const dataValue = swatchContainer.querySelector('.data-value');
        if (dataValue) {
            dataValue.textContent = optionValue.split('*')[0].trim();
        }

        const selectors = form.querySelectorAll('.single-option-selector');
        this.updateOptionSelector(selectors, optionIndex, optionValue);

        this.clearSubsequentOptions(optionIndex);
        this.updateVariantElements(optionIndex);

        if (optionValue.includes('*')) {
            this.updateCustomOptionFromMainVariant(optionValue);
        }

        this.updateCustomOption();

        requestAnimationFrame(() => {
            this.triggerVariantChange();
        });
    }

    handleCustomOptionSelection(customOptionValue) {
        const customValue = this.extractCustomValue(customOptionValue);

        const dataValueDisplay = this.dom.customOptions?.querySelector('.data-value');
        if (dataValueDisplay) {
            dataValueDisplay.textContent = customValue;
        }

        // Get the CURRENTLY selected main options
        const selectedVariants = Array.from(this.dom.selectedVariants());
        const dropdownInputs = document.querySelectorAll('.custom-dropdown .option-item input:checked');
        const allSelectedInputs = [...selectedVariants, ...dropdownInputs].slice(0, 3);

        // Get the currently selected option values
        const selectedOptions = allSelectedInputs.map((input) => input.value);
        const currentMainSelection = selectedOptions[1]; // Glass/Pattern option at index 1 (e.g., "Privacy" or "Fluted")

        // Find the main variant input that is currently selected for option 1
        const mainVariants = document.querySelectorAll('.swatch:not(.custom-option) .swatch-element input');

        let matchedInput = null;
        mainVariants.forEach((input) => {
            // Check if this is the currently selected option
            if (input.checked && this.optionValuesMatch(input.value, currentMainSelection)) {
                matchedInput = input;
            }
        });

        // Build the full value with current selection: "Privacy *Right Installation*"
        const fullCustomValue = `${currentMainSelection} *${customValue}*`;

        if (matchedInput) {
            // Update the data-full-value to reflect the selected custom option
            matchedInput.setAttribute('data-full-value', fullCustomValue);

            const swatchContainer = matchedInput.closest('.swatch');
            const optionIndex = parseInt(swatchContainer.getAttribute('data-option-index'));

            const form = matchedInput.closest('form');
            const formSelectors = form?.querySelectorAll('.single-option-selector');

            // Update the selector with just the base value
            if (formSelectors && formSelectors[optionIndex]) {
                formSelectors[optionIndex].value = matchedInput.value;
            }
        }

        requestAnimationFrame(() => {
            this.triggerVariantChange();
        });
    }

    handleSwatchChange(event) {
        if (this.isUpdating) return;

        const swatch = event.target;
        const swatchParent = swatch.parentElement;
        const swatchContainer = swatch.closest('.swatch');
        const optionIndex = parseInt(swatchContainer.getAttribute('data-option-index'));
        const optionValue = swatch.value;
        const fullValue = swatch.getAttribute('data-full-value');
        const form = swatch.closest('form');

        this.hasUserInteracted = true; // Mark that user has interacted
        const swatchElements = swatchContainer.querySelectorAll('.swatch-element');
        swatchElements.forEach((el) => el.classList.remove('selected'));
        swatchParent.classList.add('selected');

        this.updateOptionSelector(form.querySelectorAll('.single-option-selector'), optionIndex, optionValue);

        const dataValue = swatchContainer.querySelector('.header .data-value');
        if (dataValue) {
            let displayValue;
            if (optionValue.includes('*')) {
                const parts = optionValue.split('*');
                displayValue = parts[parts.length - 1].trim() || parts[0].trim();
            } else {
                displayValue = optionValue.trim();
            }
            dataValue.textContent = displayValue;
        }

        this.clearSubsequentOptions(optionIndex);

        this.updateVariantElements(optionIndex);

        // Check if this option has a custom variant (using data-full-value)
        if (fullValue && fullValue.includes('*')) {
            this.updateCustomOptionFromMainVariant(fullValue);
        }

        this.updateCustomOption();

        requestAnimationFrame(() => {
            this.triggerVariantChange();
        });
    }

    selectCustomOption(optionValue) {
        const customValue = this.extractCustomValue(optionValue);

        const customOption = document.querySelector('.swatch.custom-option');
        if (!customOption) return;

        const customInput = Array.from(customOption.querySelectorAll(`input`)).find((input) => {
            const inputCustomValue = this.extractCustomValue(input.value);

            return (
                inputCustomValue === customValue &&
                !input.closest('.swatch-element')?.classList.contains('duplicate-option')
            );
        });

        if (customInput && !customInput.checked) {
            const swatchElements = customOption.querySelectorAll('.swatch-element:not(.duplicate-option)');
            swatchElements.forEach((el) => el.classList.remove('selected'));

            customInput.checked = true;
            customInput.closest('.swatch-element')?.classList.add('selected');
        }
    }

    updateCustomOptionFromMainVariant(optionValue) {
        if (!optionValue.includes('*')) return;

        this.selectCustomOption(optionValue);
    }

    clearSubsequentOptions(fromIndex) {
        const variantProperties = this.dom.variantProperties();
        const selectors = this.dom.singleOptionSelectors();

        for (let i = fromIndex + 1; i < selectors.length; i++) {
            if (variantProperties[i]) {
                const inputs = variantProperties[i].querySelectorAll('input:checked');
                inputs.forEach((input) => {
                    input.checked = false;
                    input.closest('.swatch-element, .option-item')?.classList.remove('selected');
                });

                const dropdownItems = variantProperties[i].querySelectorAll('.option-item input:checked');
                dropdownItems.forEach((input) => {
                    input.checked = false;
                    input.closest('.option-item')?.classList.remove('selected');
                });

                const swatchContainer = variantProperties[i].closest('.swatch');
                const dropdown = swatchContainer?.querySelector('.custom-dropdown');
                if (dropdown) {
                    const selectedOption = dropdown.querySelector('.selected-option');
                    if (selectedOption) {
                        selectedOption.textContent = 'Select an option';
                    }
                }
            }

            if (selectors[i]) {
                if (selectors[i].tagName === 'SELECT') {
                    selectors[i].selectedIndex = 0;
                    selectors[i].value = '';
                } else {
                    selectors[i].value = '';
                }
            }
        }
    }

    handleNavigationChange() {
        const currentVariantId = this.getUrlVariantId() || this.data.defaultVariantId;

        if (this.prevVariantId !== currentVariantId) {
            this.formatImageData();
            this.rerenderOptions();
            this.updateCustomOption();
        }

        this.prevVariantId = currentVariantId;
    }

    updateOptionSelector(selectors, index, value) {
        if (selectors[index]) {
            selectors[index].value = value;

            if (selectors[index].tagName === 'SELECT') {
                const option = Array.from(selectors[index].options).find((opt) =>
                    this.optionValuesMatch(opt.value, value),
                );
                if (option) {
                    selectors[index].value = option.value;
                }
            }

            selectors[index].dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    updateVariantElements(changedOptionIndex = null) {
        if (this.isUpdating) return;
        this.isUpdating = true;

        const productData = this.data.variants.map((variant) => ({
            id: variant.id,
            title: variant.title,
            options: variant.options,
            available: variant.available,
        }));

        const variantId = this.getUrlVariantId() || productData[0].id;
        this.loadVariantData(variantId);

        const variantFromUrl = productData.find((product) => product.id === parseInt(variantId));
        const selectedVariants = this.dom.selectedVariants();

        if (selectedVariants.length === 0) {
            this.isUpdating = false;
            return;
        }

        const selectedVariantFromOption = selectedVariants[0].value;
        const variantByOption = productData.find((product) => product.options[0] === selectedVariantFromOption);

        if (!variantFromUrl || !variantByOption) {
            this.isUpdating = false;
            return;
        }

        const variant = variantFromUrl.options[0] !== variantByOption.options[0] ? variantByOption : variantFromUrl;

        if (!variant) {
            this.isUpdating = false;
            return;
        }

        const currentOption = variant.options[0];
        const sameOptionProducts = productData.filter((product) => product.options[0] === currentOption);

        this.updateVariantProperties(sameOptionProducts, changedOptionIndex);

        this.isUpdating = false;
    }

    updateVariantProperties(sameOptionProducts, changedOptionIndex = null) {
        const variantProperties = this.dom.variantProperties();

        // Hierarchical rendering: only update options that come AFTER the changed option
        // Never update options that come before (upper options)

        // Update option 2 (glass) only if option 1 (color) changed or on initial load
        if (variantProperties.length > 1 && (changedOptionIndex === null || changedOptionIndex === 0)) {
            this.handleSecondVariant(variantProperties[1], sameOptionProducts);
        }

        // Update option 3 (size) only if option 1 or 2 changed, or on initial load
        if (variantProperties.length > 2 && (changedOptionIndex === null || changedOptionIndex <= 1)) {
            this.handleThirdVariant(variantProperties, sameOptionProducts);
        }

        // If option 3+ (size or later) changed, update options 4+ (if they exist)
        if (variantProperties.length > 2 && changedOptionIndex >= 2) {
            const secondVariantInput = variantProperties[1].querySelector('input:checked');
            if (secondVariantInput) {
                const selectedSecondValue =
                    secondVariantInput.parentElement.dataset.value ||
                    secondVariantInput.closest('.option-item')?.dataset.value;
                const filteredProducts = sameOptionProducts.filter(
                    (product) => product.options[1] && this.optionValuesMatch(product.options[1], selectedSecondValue),
                );

                // Only update 4th option if it exists
                if (variantProperties.length > 3) {
                    this.handleFourthVariant(
                        variantProperties,
                        sameOptionProducts,
                        filteredProducts,
                        selectedSecondValue,
                    );
                }
            }
        }
    }

    optionValuesMatch(value1, value2) {
        if (!value1 || !value2) return false;

        const cleanValue1 = value1.split('*')[0].trim();
        const cleanValue2 = value2.split('*')[0].trim();

        if (cleanValue1.toLowerCase() === cleanValue2.toLowerCase()) {
            return true;
        }

        const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (normalize(cleanValue1) === normalize(cleanValue2)) {
            return true;
        }

        return false;
    }

    handleSecondVariant(secondVariantProperty, sameOptionProducts) {
        // Hierarchical rendering: Show ALL glass options available for the selected color
        // Do NOT filter by size - size selection shouldn't affect what glass options are shown
        const variantProperties = this.dom.variantProperties();
        const selectors = this.dom.singleOptionSelectors();

        const availableOptionsFromProducts = new Map();
        sameOptionProducts.forEach((product) => {
            if (product.options[1] && !this.isHiddenOption(product.options[1])) {
                const optionValue = product.options[1];
                if (!availableOptionsFromProducts.has(optionValue)) {
                    availableOptionsFromProducts.set(optionValue, product);
                }
            }
        });

        const shouldShow = this.shouldShowSwatchContainer(secondVariantProperty, availableOptionsFromProducts);
        const swatchContainer = secondVariantProperty.closest('.swatch');
        if (swatchContainer) {
            if (shouldShow) {
                swatchContainer.style.display = '';
                swatchContainer.classList.remove('hidden-option');
            } else {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }
        }

        if (!shouldShow) {
            if (selectors[1]) {
                selectors[1].value = '';
                if (selectors[1].tagName === 'SELECT') {
                    selectors[1].selectedIndex = 0;
                }
            }
            secondVariantProperty.querySelectorAll('input:checked').forEach((input) => {
                input.checked = false;
                input.closest('.swatch-element, .option-item')?.classList.remove('selected');
            });
            return;
        }

        // Check if this is a dropdown or regular swatches
        const isDropdown = swatchContainer?.querySelector('.custom-dropdown') !== null;
        const hasDropdownElements =
            secondVariantProperty && secondVariantProperty.querySelectorAll('.option-item').length > 0;

        const selectedSecondVariant =
            isDropdown || hasDropdownElements
                ? Array.from(secondVariantProperty.querySelectorAll('.option-item:not(.hidden-option)'))
                : Array.from(secondVariantProperty.querySelectorAll('.swatch-element:not(.hidden-option)'));

        const currentlyChecked = secondVariantProperty.querySelector('input:checked');
        const secondSelector = selectors[1];

        let needsSelection = !currentlyChecked || this.isHiddenOption(currentlyChecked.value);
        let firstAvailableVariant = null;

        selectedSecondVariant.forEach((el) => {
            const elValue = el.dataset.value;
            const input = el.querySelector('input');

            if (this.isHiddenOption(elValue)) {
                el.style.display = 'none';
                return;
            }

            const isAvailable = Array.from(availableOptionsFromProducts.keys()).some((key) => {
                const matches = this.optionValuesMatch(key, elValue);
                return matches;
            });

            // Check if this is a base option (like "Included") that has custom variants with asterisks
            const availableKeys = Array.from(availableOptionsFromProducts.keys());
            const matchingKeysWithAsterisk = availableKeys.filter(
                (key) => key.includes('*') && this.optionValuesMatch(key, elValue),
            );

            // Check if there's a non-asterisk version available
            const hasNonAsteriskVersion = availableKeys.some(
                (key) => !key.includes('*') && this.optionValuesMatch(key, elValue),
            );

            // If this base option has custom variants, set data-full-value to trigger custom option
            if (matchingKeysWithAsterisk.length > 0 && !elValue.includes('*') && !hasNonAsteriskVersion) {
                if (input) {
                    // Set the first matching variant with asterisk as the full value
                    input.setAttribute('data-full-value', matchingKeysWithAsterisk[0]);
                }
            } else if (hasNonAsteriskVersion || matchingKeysWithAsterisk.length === 0) {
                // Clear data-full-value if we now have a non-asterisk version or no asterisk versions
                if (input && input.hasAttribute('data-full-value')) {
                    input.removeAttribute('data-full-value');
                }
            }

            if (!isAvailable && el.querySelector('input:checked')) {
                const input = el.querySelector('input');
                if (input) {
                    input.checked = false;
                    el.classList.remove('selected');
                    needsSelection = true;
                }
            }

            this.updateVariantElementState(el, isAvailable, sameOptionProducts);

            if (isAvailable && !firstAvailableVariant) {
                firstAvailableVariant = el;
            }
        });

        if (secondSelector && secondSelector.tagName === 'SELECT') {
            const valueChanged = this.updateDropdownOptionsFromProducts(
                secondSelector,
                availableOptionsFromProducts,
                1,
            );

            if (valueChanged && secondSelector.value) {
                selectors[1].value = secondSelector.value;
                needsSelection = true;
            }
        }

        if (needsSelection && firstAvailableVariant) {
            const input = firstAvailableVariant.querySelector('input');
            if (input) {
                selectedSecondVariant.forEach((el) => el.classList.remove('selected'));
                firstAvailableVariant.classList.add('selected');
                input.checked = true;

                const form = input.closest('form');
                const formSelectors = form?.querySelectorAll('.single-option-selector');
                if (formSelectors && formSelectors[1]) {
                    formSelectors[1].value = input.value;
                }

                if (secondSelector && secondSelector.tagName === 'SELECT') {
                    const matchingOption = Array.from(secondSelector.options).find((opt) =>
                        this.optionValuesMatch(opt.value, input.value),
                    );
                    if (matchingOption) {
                        secondSelector.value = matchingOption.value;
                        secondSelector.selectedIndex = Array.from(secondSelector.options).indexOf(matchingOption);
                        this.updateCustomDropdownDisplay(secondSelector, matchingOption.text);
                        secondSelector.dispatchEvent(new Event('input', { bubbles: false }));
                    }
                }
            }
        }
    }

    updateDropdownOptionsFromProducts(selectElement, availableOptionsMap, optionIndex) {
        const currentValue = selectElement.value;
        let hasValidSelection = false;
        let firstValidOption = null;

        Array.from(selectElement.options).forEach((option, index) => {
            if (index === 0 || !option.value) {
                option.disabled = false;
                option.style.display = '';
                option.hidden = false;
                return;
            }

            if (this.isHiddenOption(option.value)) {
                option.disabled = true;
                option.hidden = true;
                option.style.display = 'none';
                return;
            }

            const isAvailable = Array.from(availableOptionsMap.keys()).some((productOption) =>
                this.optionValuesMatch(option.value, productOption),
            );

            option.disabled = !isAvailable;
            option.hidden = !isAvailable;
            option.style.display = isAvailable ? '' : 'none';

            if (isAvailable) {
                if (!firstValidOption) {
                    firstValidOption = option;
                }
                if (this.optionValuesMatch(option.value, currentValue)) {
                    hasValidSelection = true;
                }
            }
        });

        if (!hasValidSelection && firstValidOption) {
            selectElement.value = firstValidOption.value;
            selectElement.selectedIndex = Array.from(selectElement.options).indexOf(firstValidOption);

            this.updateCustomDropdownDisplay(selectElement, firstValidOption.text);

            selectElement.dispatchEvent(new Event('input', { bubbles: false }));

            return true;
        } else if (!hasValidSelection && !firstValidOption) {
            selectElement.selectedIndex = 0;
            selectElement.value = '';

            const placeholderOption = selectElement.options[0];
            if (placeholderOption) {
                this.updateCustomDropdownDisplay(selectElement, placeholderOption.text);
            }

            selectElement.dispatchEvent(new Event('input', { bubbles: false }));

            return true;
        } else {
            return false;
        }
    }

    updateCustomDropdownDisplay(selectElement, displayText) {
        const form = selectElement.closest('form');
        if (form) {
            const selectors = form.querySelectorAll('.single-option-selector');
            const selectIndex = Array.from(selectors).indexOf(selectElement);

            if (selectIndex >= 0) {
                const swatch = form.querySelector(`.swatch[data-option-index="${selectIndex}"]`);
                if (swatch) {
                    const customDropdown = swatch.querySelector('.custom-dropdown');
                    if (customDropdown) {
                        const dropdownHeader = customDropdown.querySelector('.dropdown-header');
                        if (dropdownHeader) {
                            const selectedOption = dropdownHeader.querySelector('.selected-option');
                            if (selectedOption) {
                                selectedOption.textContent = displayText;
                                customDropdown.classList.remove('active');
                                return;
                            }
                        }
                    }

                    const dataValue = swatch.querySelector('.header .data-value');
                    if (dataValue) {
                        dataValue.textContent = displayText;
                    }
                }
            }
        }

        const customDropdown = selectElement.closest('.custom-dropdown, .variant-property');
        if (customDropdown) {
            const dropdownHeader = customDropdown.querySelector('.dropdown-header');
            if (dropdownHeader) {
                const selectedOption = dropdownHeader.querySelector('.selected-option');
                if (selectedOption) {
                    selectedOption.textContent = displayText;
                    customDropdown.classList.remove('active');
                }
            }
        }
    }

    updateDropdownOptions(selectElement, availableOptions, products) {
        const currentValue = selectElement.value;
        let hasValidSelection = false;

        Array.from(selectElement.options).forEach((option, index) => {
            if (index === 0 || !option.value) {
                option.disabled = false;
                return;
            }

            if (this.isHiddenOption(option.value)) {
                option.disabled = true;
                option.style.display = 'none';
                return;
            }

            const isAvailable = Array.from(availableOptions).some((available) =>
                this.optionValuesMatch(available, option.value),
            );

            option.disabled = !isAvailable;
            option.style.display = isAvailable ? '' : 'none';

            if (isAvailable && this.optionValuesMatch(option.value, currentValue)) {
                hasValidSelection = true;
            }
        });

        if (!hasValidSelection && availableOptions.size > 0) {
            const firstAvailable = Array.from(availableOptions)[0];
            const matchingOption = Array.from(selectElement.options).find((opt) =>
                this.optionValuesMatch(opt.value, firstAvailable),
            );

            if (matchingOption) {
                selectElement.value = matchingOption.value;
            }
        } else if (!hasValidSelection) {
            selectElement.selectedIndex = 0;
            selectElement.value = '';
        }
    }

    updateVariantElementState(element, isAvailable, products) {
        if (isAvailable) {
            element.classList.remove('disabled');
            element.style.display = 'flex';
            const input = element.querySelector('input');
            if (input) input.disabled = false;

            const isShowerPanOption = element.classList.contains('option-item');

            if (products[0].options.length === 2 || isShowerPanOption) {
                const elValue = element.dataset.value;
                const hasAvailableProduct = products.some(
                    (product) =>
                        product.available && product.options[1] && this.optionValuesMatch(product.options[1], elValue),
                );
                element.classList.toggle('soldout', !hasAvailableProduct);
                element.classList.toggle('available', hasAvailableProduct);
            }
        } else {
            element.classList.add('disabled');
            element.style.display = 'none';
            const input = element.querySelector('input');
            if (input) input.disabled = true;
        }
    }

    handleThirdVariant(variantProperties, sameOptionProducts) {
        const secondVariantInput = variantProperties[1].querySelector('input:checked');
        const selectors = this.dom.singleOptionSelectors();

        if (!secondVariantInput) {
            this.hideAllThirdAndFourthOptions(variantProperties);
            return;
        }

        const selectedSecondValue =
            secondVariantInput.parentElement.dataset.value || secondVariantInput.closest('.option-item')?.dataset.value;

        const filteredProducts = sameOptionProducts.filter(
            (product) => product.options[1] && this.optionValuesMatch(product.options[1], selectedSecondValue),
        );

        this.handleThirdVariantSelection(variantProperties, filteredProducts);
        this.handleFourthVariant(variantProperties, sameOptionProducts, filteredProducts, selectedSecondValue);
    }

    hideAllThirdAndFourthOptions(variantProperties) {
        const selectors = this.dom.singleOptionSelectors();

        if (variantProperties.length > 2) {
            const thirdProperty = variantProperties[2];
            const swatchContainer = thirdProperty.closest('.swatch');
            if (swatchContainer) {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }

            const thirdOptions = thirdProperty.querySelectorAll('.swatch-element');
            thirdOptions.forEach((el) => {
                el.style.display = 'none';
                el.classList.add('disabled');
                const input = el.querySelector('input');
                if (input) {
                    input.disabled = true;
                    if (input.checked) {
                        input.checked = false;
                        el.classList.remove('selected');
                    }
                }
            });

            if (selectors[2]) {
                if (selectors[2].tagName === 'SELECT') {
                    selectors[2].selectedIndex = 0;
                }
                selectors[2].value = '';
            }
        }

        if (variantProperties.length > 3) {
            const fourthProperty = variantProperties[3];
            const swatchContainer = fourthProperty.closest('.swatch');
            if (swatchContainer) {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }

            const fourthOptions = fourthProperty.querySelectorAll('.swatch-element');
            fourthOptions.forEach((el) => {
                el.style.display = 'none';
                el.classList.add('disabled');
                const input = el.querySelector('input');
                if (input) {
                    input.disabled = true;
                    if (input.checked) {
                        input.checked = false;
                        el.classList.remove('selected');
                    }
                }
            });

            if (selectors[3]) {
                if (selectors[3].tagName === 'SELECT') {
                    selectors[3].selectedIndex = 0;
                }
                selectors[3].value = '';
            }
        }
    }

    handleThirdVariantSelection(variantProperties, filteredProducts) {
        if (variantProperties.length <= 2) return;

        const thirdProperty = variantProperties[2];
        const selectors = this.dom.singleOptionSelectors();
        const thirdSelector = selectors[2];

        // Filter products based on the second option selection
        let productsToCheck = filteredProducts.filter(
            (product) => product.options[2] && !this.isHiddenOption(product.options[2]),
        );

        if (variantProperties.length > 3 && selectors[3]) {
            const fourthValue = selectors[3].value;
            if (fourthValue && !this.isHiddenOption(fourthValue)) {
                productsToCheck = productsToCheck.filter(
                    (product) => product.options[3] && this.optionValuesMatch(product.options[3], fourthValue),
                );
            }
        }

        const availableOptionsFromProducts = new Map();
        productsToCheck.forEach((product) => {
            if (product.options[2] && !this.isHiddenOption(product.options[2])) {
                const optionValue = product.options[2];
                if (!availableOptionsFromProducts.has(optionValue)) {
                    availableOptionsFromProducts.set(optionValue, product);
                }
            }
        });

        const shouldShow = this.shouldShowSwatchContainer(thirdProperty, availableOptionsFromProducts);
        const swatchContainer = thirdProperty.closest('.swatch');
        if (swatchContainer) {
            if (shouldShow) {
                swatchContainer.style.display = '';
                swatchContainer.classList.remove('hidden-option');
            } else {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }
        }

        if (!shouldShow) {
            if (selectors[2]) {
                selectors[2].value = '';
                if (selectors[2].tagName === 'SELECT') {
                    selectors[2].selectedIndex = 0;
                }
            }
            thirdProperty.querySelectorAll('input:checked').forEach((input) => {
                input.checked = false;
                input.closest('.swatch-element, .option-item')?.classList.remove('selected');
            });
            return;
        }

        const isDropdown = swatchContainer?.querySelector('.custom-dropdown') !== null;
        const hasSwatchElements = thirdProperty && thirdProperty.querySelectorAll('.swatch-element').length > 0;
        const hasDropdownElements = thirdProperty && thirdProperty.querySelectorAll('.option-item').length > 0;

        if (!hasSwatchElements && !hasDropdownElements && thirdSelector && thirdSelector.tagName === 'SELECT') {
            const valueChanged = this.updateDropdownOptionsFromProducts(thirdSelector, availableOptionsFromProducts, 2);

            if (thirdSelector.value && selectors[2]) {
                selectors[2].value = thirdSelector.value;
            }

            return;
        }

        const selectedThirdVariant =
            isDropdown || hasDropdownElements
                ? Array.from(thirdProperty.querySelectorAll('.option-item:not(.hidden-option)'))
                : Array.from(thirdProperty.querySelectorAll('.swatch-element:not(.hidden-option)'));

        const currentlyCheckedInput = thirdProperty.querySelector('input:checked');

        let needsSelection = false;
        let firstAvailableVariant = null;
        let hasAnyAvailable = false;

        if (currentlyCheckedInput && this.isHiddenOption(currentlyCheckedInput.value)) {
            currentlyCheckedInput.checked = false;
            currentlyCheckedInput.closest('.swatch-element, .option-item').classList.remove('selected');
            needsSelection = true;
        } else if (currentlyCheckedInput) {
            const currentValue =
                currentlyCheckedInput.parentElement.dataset.value ||
                currentlyCheckedInput.closest('.option-item')?.dataset.value;
            const isCurrentValid = Array.from(availableOptionsFromProducts.keys()).some((key) =>
                this.optionValuesMatch(key, currentValue),
            );

            if (!isCurrentValid) {
                currentlyCheckedInput.checked = false;
                currentlyCheckedInput.closest('.swatch-element, .option-item').classList.remove('selected');
                needsSelection = true;
            }
        } else {
            needsSelection = true;
        }

        selectedThirdVariant.forEach((el) => {
            const elValue = el.dataset.value;

            if (this.isHiddenOption(elValue)) {
                el.style.display = 'none';
                return;
            }

            const isAvailable = Array.from(availableOptionsFromProducts.keys()).some((key) =>
                this.optionValuesMatch(key, elValue),
            );

            const isAvailableAndInStock =
                isAvailable &&
                productsToCheck.some(
                    (product) =>
                        product.options[2] && this.optionValuesMatch(product.options[2], elValue) && product.available,
                );

            if (isDropdown || hasDropdownElements) {
                if (isAvailable) {
                    el.classList.remove('disabled');
                    el.style.display = 'block';
                    const input = el.querySelector('input');
                    if (input) input.disabled = false;
                    el.classList.toggle('soldout', !isAvailableAndInStock);
                    el.classList.toggle('available', isAvailableAndInStock);
                } else {
                    el.classList.add('disabled');
                    el.style.display = 'none';
                    const input = el.querySelector('input');
                    if (input) input.disabled = true;
                }
            } else {
                this.updateThirdVariantState(el, isAvailable, isAvailableAndInStock);
            }

            if (isAvailable) {
                hasAnyAvailable = true;
                if (!firstAvailableVariant) {
                    firstAvailableVariant = el;
                }
            }
        });

        if (thirdSelector && thirdSelector.tagName === 'SELECT') {
            const valueChanged = this.updateDropdownOptionsFromProducts(thirdSelector, availableOptionsFromProducts, 2);

            if (valueChanged && thirdSelector.value) {
                selectors[2].value = thirdSelector.value;
                needsSelection = true;
            }
        }

        if (needsSelection && firstAvailableVariant) {
            const input = firstAvailableVariant.querySelector('input');
            if (input) {
                selectedThirdVariant.forEach((el) => el.classList.remove('selected'));
                firstAvailableVariant.classList.add('selected');
                input.checked = true;

                const form = input.closest('form');
                const formSelectors = form?.querySelectorAll('.single-option-selector');
                if (formSelectors && formSelectors[2]) {
                    formSelectors[2].value = input.value;
                }

                if (thirdSelector && thirdSelector.tagName === 'SELECT') {
                    const matchingOption = Array.from(thirdSelector.options).find((opt) =>
                        this.optionValuesMatch(opt.value, input.value),
                    );
                    if (matchingOption) {
                        thirdSelector.value = matchingOption.value;
                        thirdSelector.selectedIndex = Array.from(thirdSelector.options).indexOf(matchingOption);
                        this.updateCustomDropdownDisplay(thirdSelector, matchingOption.text);
                        thirdSelector.dispatchEvent(new Event('input', { bubbles: false }));
                    }
                }

                if (isDropdown) {
                    const dropdown = swatchContainer.querySelector('.custom-dropdown');
                    const label = input.nextElementSibling;
                    const optionText =
                        label?.querySelector('.option-text')?.textContent || input.value.split('*')[0].trim();
                    const selectedOption = dropdown?.querySelector('.selected-option');

                    if (selectedOption) {
                        selectedOption.textContent = optionText;
                    }
                }
            }
        } else if (needsSelection && !firstAvailableVariant && thirdSelector && thirdSelector.value) {
            if (selectors[2]) {
                selectors[2].value = thirdSelector.value;
            }
        }
    }

    updateThirdVariantState(element, isAvailable, isAvailableAndInStock) {
        if (isAvailable) {
            element.classList.remove('disabled');
            element.style.display = 'flex';
            const input = element.querySelector('input');
            if (input) input.disabled = false;
            element.classList.toggle('soldout', !isAvailableAndInStock);
            element.classList.toggle('available', isAvailableAndInStock);
        } else {
            element.classList.add('disabled');
            element.style.display = 'none';
            const input = element.querySelector('input');
            if (input) input.disabled = true;
        }
    }

    handleFourthVariant(variantProperties, sameOptionProducts, filteredProducts, selectedSecondValue) {
        if (variantProperties.length <= 3) return;

        const fourthVariantProperty = variantProperties[3];
        const selectedThirdInput = variantProperties[2].querySelector('input:checked');
        const selectedThirdValue =
            selectedThirdInput?.parentElement.dataset.value ||
            selectedThirdInput?.closest('.option-item')?.dataset.value;
        const selectors = this.dom.singleOptionSelectors();
        const fourthSelector = selectors[3];
        const swatchContainer = fourthVariantProperty.closest('.swatch');

        const isDropdown = swatchContainer?.querySelector('.custom-dropdown') !== null;

        const selectedFourthVariant = isDropdown
            ? Array.from(fourthVariantProperty.querySelectorAll('.option-item:not(.hidden-option)'))
            : Array.from(fourthVariantProperty.querySelectorAll('.swatch-element:not(.hidden-option)'));

        if (!selectedThirdValue || this.isHiddenOption(selectedThirdValue)) {
            if (swatchContainer) {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }

            selectedFourthVariant.forEach((el) => {
                el.style.display = 'none';
                el.classList.add('disabled');
                const input = el.querySelector('input');
                if (input) {
                    input.disabled = true;
                    if (input.checked) {
                        input.checked = false;
                        el.classList.remove('selected');
                    }
                }
            });

            if (fourthSelector) {
                if (fourthSelector.tagName === 'SELECT') {
                    fourthSelector.selectedIndex = 0;
                }
                fourthSelector.value = '';
            }
            return;
        }

        const fourthFilteredProducts = filteredProducts.filter(
            (product) =>
                product.options[2] &&
                this.optionValuesMatch(product.options[2], selectedThirdValue) &&
                product.options[1] &&
                this.optionValuesMatch(product.options[1], selectedSecondValue) &&
                (!product.options[3] || !this.isHiddenOption(product.options[3])),
        );

        const availableOptionsFromProducts = new Map();
        fourthFilteredProducts.forEach((product) => {
            if (product.options[3] && !this.isHiddenOption(product.options[3])) {
                const optionValue = product.options[3];
                if (!availableOptionsFromProducts.has(optionValue)) {
                    availableOptionsFromProducts.set(optionValue, product);
                }
            }
        });

        const shouldShow = this.shouldShowSwatchContainer(fourthVariantProperty, availableOptionsFromProducts);
        if (swatchContainer) {
            if (shouldShow) {
                swatchContainer.style.display = '';
                swatchContainer.classList.remove('hidden-option');
            } else {
                swatchContainer.style.display = 'none';
                swatchContainer.classList.add('hidden-option');
            }
        }

        if (!shouldShow) {
            if (fourthSelector) {
                fourthSelector.value = '';
                if (fourthSelector.tagName === 'SELECT') {
                    fourthSelector.selectedIndex = 0;
                }
            }
            selectedFourthVariant.forEach((el) => {
                const input = el.querySelector('input');
                if (input && input.checked) {
                    input.checked = false;
                    el.classList.remove('selected');
                }
            });
            return;
        }

        let needsSelection = false;
        let firstAvailableVariant = null;
        let hasAnyVisible = false;
        const currentlyChecked = fourthVariantProperty.querySelector('input:checked');

        selectedFourthVariant.forEach((el) => {
            const input = el.querySelector('input');
            const inputValue = input.value;

            if (this.isHiddenOption(inputValue)) {
                el.style.display = 'none';
                return;
            }

            const isAvailable =
                Array.from(availableOptionsFromProducts.keys()).some((key) =>
                    this.optionValuesMatch(key, inputValue),
                ) ||
                fourthFilteredProducts.some((product) =>
                    product.title.toLowerCase().includes(inputValue.toLowerCase()),
                );

            if (input.checked && !isAvailable) {
                input.checked = false;
                el.classList.remove('selected');
                needsSelection = true;
            }

            if (isAvailable) {
                el.style.display = isDropdown ? 'block' : 'flex';
                el.classList.remove('disabled');
                input.disabled = false;
                hasAnyVisible = true;

                if (!firstAvailableVariant) {
                    firstAvailableVariant = el;
                }

                // Check if there are any available products in stock for this option
                const hasAvailableProduct = fourthFilteredProducts.some((product) => {
                    if (!product.available) return false;
                    if (!product.options[3]) return false;

                    // For custom options with asterisks, do exact match
                    if (product.options[3].includes('*') && inputValue.includes('*')) {
                        return (
                            product.options[3].toLowerCase().replace(/\s+/g, ' ').trim() ===
                            inputValue.toLowerCase().replace(/\s+/g, ' ').trim()
                        );
                    }

                    // Otherwise use the existing optionValuesMatch logic
                    return this.optionValuesMatch(product.options[3], inputValue);
                });

                // Add soldout/available classes for both dropdown and swatch elements
                el.classList.toggle('soldout', !hasAvailableProduct);
                el.classList.toggle('available', hasAvailableProduct);
            } else {
                el.style.display = 'none';
                el.classList.add('disabled');
                input.disabled = true;
            }
        });

        if (fourthSelector && fourthSelector.tagName === 'SELECT') {
            this.updateDropdownOptionsFromProducts(fourthSelector, availableOptionsFromProducts, 3);

            const currentValue = fourthSelector.value;
            const isCurrentValid =
                currentValue &&
                !this.isHiddenOption(currentValue) &&
                Array.from(availableOptionsFromProducts.keys()).some((key) =>
                    this.optionValuesMatch(key, currentValue),
                );

            if (!isCurrentValid) {
                needsSelection = true;
            }
        }

        if (!currentlyChecked && firstAvailableVariant) {
            needsSelection = true;
        }

        if (currentlyChecked && !needsSelection) {
            const currentInput = currentlyChecked;
            const currentValue = currentInput.value;
            const isStillAvailable = Array.from(availableOptionsFromProducts.keys()).some((key) =>
                this.optionValuesMatch(key, currentValue),
            );

            if (isStillAvailable) {
                if (!isDropdown) {
                    const dataValue = swatchContainer?.querySelector('.header .data-value');
                    if (dataValue) {
                        let displayValue;
                        if (currentValue.includes('*')) {
                            const parts = currentValue.split('*');
                            displayValue = parts[parts.length - 1].trim() || parts[0].trim();
                        } else {
                            displayValue = currentValue.trim();
                        }
                        dataValue.textContent = displayValue;
                    }
                } else {
                    const dropdown = swatchContainer.querySelector('.custom-dropdown');
                    const label = currentInput.nextElementSibling;
                    const optionText =
                        label?.querySelector('.option-text')?.textContent || currentValue.split('*')[0].trim();
                    const selectedOption = dropdown?.querySelector('.selected-option');

                    if (selectedOption) {
                        selectedOption.textContent = optionText;
                    }
                }
            }
        }

        if (needsSelection && firstAvailableVariant) {
            const input = firstAvailableVariant.querySelector('input');
            selectedFourthVariant.forEach((el) => el.classList.remove('selected'));
            firstAvailableVariant.classList.add('selected');
            input.checked = true;

            const form = input.closest('form');
            const formSelectors = form?.querySelectorAll('.single-option-selector');
            if (formSelectors && formSelectors[3]) {
                formSelectors[3].value = input.value;
            }

            if (!isDropdown) {
                const dataValue = swatchContainer?.querySelector('.header .data-value');
                if (dataValue) {
                    let displayValue;
                    if (input.value.includes('*')) {
                        const parts = input.value.split('*');
                        displayValue = parts[parts.length - 1].trim() || parts[0].trim();
                    } else {
                        displayValue = input.value.trim();
                    }
                    dataValue.textContent = displayValue;
                }
            }

            if (isDropdown) {
                const dropdown = swatchContainer.querySelector('.custom-dropdown');
                const label = input.nextElementSibling;
                const optionText =
                    label?.querySelector('.option-text')?.textContent || input.value.split('*')[0].trim();
                const selectedOption = dropdown?.querySelector('.selected-option');

                if (selectedOption) {
                    selectedOption.textContent = optionText;
                }
            }
        }
    }

    formatImageData() {
        const variant = this.getCurrentVariant();
        if (!variant) return;

        const variantSku = variant.sku;
        const images = this.collectImageGroups();
        const filteredImages = images[variantSku] || [];

        if (variant.featured_image) {
            const variantFeaturedImageSrc = variant.featured_image.src.split('?')[0];
            const isFeaturedImageInFilteredImages = filteredImages.some(
                (image) => image.src.split('?')[0] === variantFeaturedImageSrc,
            );

            if (!isFeaturedImageInFilteredImages) {
                filteredImages.unshift({
                    src: variant.featured_image.src,
                    width: variant.featured_image.width,
                    height: variant.featured_image.height,
                    alt: variant.featured_image.alt,
                });
            }
        }

        if (images['video']) {
            filteredImages.push(...images['video']);
        }

        this.updateMainImages(filteredImages);
        this.updateModalSwiper(filteredImages);
    }

    updateModalSwiper(images) {
        if (!this.dom.modalSwiper || !this.dom.modalThumbs) return;

        this.dom.modalSwiper.innerHTML = '';
        this.dom.modalThumbs.innerHTML = '';

        const hasVideo = images.some((image) => image.mediaType === 'video');

        const videoTab = document.querySelector('.image-type-tab[data-tab="videos"]');
        if (videoTab) {
            videoTab.style.display = hasVideo ? 'block' : 'none';
        }

        images.forEach((image, index) => {
            const swiperSlide = this.createModalSwiperSlide(image);
            this.dom.modalSwiper.innerHTML += swiperSlide;

            const thumbnail = this.createModalThumbnail(image, index);
            this.dom.modalThumbs.innerHTML += thumbnail;
        });

        this.initModalSwiperInstance();
    }

    createModalSwiperSlide(image) {
        if (image.mediaType === 'video') {
            const videoSource = image.videoSources?.find((source) => source.format === 'mp4');
            return `
            <swiper-slide>
                <div class="image-thumbnail-container">
                    <img
                        src="${image.src}"
                        width="${image.width}"
                        height="${image.height}"
                        alt="${image.alt}"
                        class="image-thumbnail"
                        style="width: 100%; height: 100%;"
                    >
                    <button
                        type="button"
                        class="round-icon-btn-lg btn-colors-primary btn-hover-default icon-btn-focus"
                        tabindex="0"
                        aria-label="Play media"
                        onclick="playVideo(event)"
                    >
                        <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 6L0.500001 12L0.500002 -4.58969e-07L11 6Z" fill="white"/>
                        </svg>
                    </button>
                    <video class="product-video" src="${videoSource ? videoSource.url : ''}" width="100%" height="100%" controls></video>
                </div>
            </swiper-slide>
        `;
        } else {
            return `
            <swiper-slide>
                <div class="image-thumbnail-container" style="width: 100%; height: 100%;">
                    <img
                        src="${image.src}"
                        width="${image.width}"
                        height="${image.height}"
                        alt="${image.alt}"
                        class="image-thumbnail"
                        style="width: 100%; height: 100%;"
                    >
                </div>
            </swiper-slide>
        `;
        }
    }

    createModalThumbnail(image, index) {
        if (image.mediaType === 'video') {
            return `
            <img
                src="${image.src}"
                width="80px"
                height="80px"
                alt="${image.alt}"
                class="image-thumbnail"
                style="width: 80px; height: 80px; object-fit: contain;"
                data-slide-index="${index}"
            >
        `;
        } else {
            return `
            <img
                src="${image.src}"
                width="80px"
                height="80px"
                alt="${image.alt}"
                class="image-thumbnail"
                style="width: 80px; height: 80px;"
                data-slide-index="${index}"
            >
        `;
        }
    }

    initModalSwiperInstance() {
        if (!this.dom.modalSwiper) return;

        const swiperParams = {
            slidesPerView: 1,
            rebuildOnUpdate: true,
            effect: 'fade',
            allowTouchMove: false,
            observer: true,
            speed: 1,
            on: {
                slideChange: (swiper, event) => {
                    const imageModal = document.querySelector('.image-modal');
                    if (imageModal) {
                        const swiperSlide = swiper.slides[swiper.previousIndex];
                        const videoSlide = swiperSlide?.querySelector('.product-video');
                        if (videoSlide) {
                            videoSlide.pause();
                            videoSlide.currentTime = 0;
                        }
                    }

                    // Update thumbnail active state
                    this.updateActiveThumbnail(swiper.activeIndex);
                },
            },
        };

        Object.assign(this.dom.modalSwiper, swiperParams);

        if (this.dom.modalSwiper.initialize) {
            this.dom.modalSwiper.initialize();
        }

        // Don't automatically reset to slide 0 - let openModalAtSlide handle it
        this.setupThumbnailClickHandlers();
    }

    updateActiveThumbnail(activeIndex) {
        if (!this.dom.modalThumbs) return;

        const thumbImages = this.dom.modalThumbs.querySelectorAll('img[data-slide-index]');
        thumbImages.forEach((thumb, idx) => {
            thumb.classList.remove('active');
            if (idx === activeIndex) {
                thumb.classList.add('active');
            }
        });
    }

    setupThumbnailClickHandlers() {
        if (!this.dom.modalThumbs || !this.dom.modalSwiper) return;

        const thumbImages = this.dom.modalThumbs.querySelectorAll('img[data-slide-index]');

        // Don't set initial active state here - let openModalAtSlide handle it
        thumbImages.forEach((thumb) => {
            thumb.addEventListener('click', () => {
                const index = parseInt(thumb.getAttribute('data-slide-index'));

                // Update active state
                this.updateActiveThumbnail(index);

                if (this.dom.modalSwiper.swiper) {
                    this.dom.modalSwiper.swiper.slideTo(index);
                }
            });
        });
    }

    collectImageGroups() {
        const imageGroups = {};

        this.data.media.forEach((media) => {
            const skus = media.alt.split('/');
            skus.forEach((sku) => {
                if (!imageGroups[sku]) {
                    imageGroups[sku] = [];
                }
                imageGroups[sku].push({
                    src: media.src,
                    width: media.width,
                    height: media.height,
                    alt: media.alt,
                    mediaType: media.mediaType,
                    videoSources: media.videoSources,
                });
            });
        });

        this.data.variantImages.forEach((variantData) => {
            const sku = variantData.sku;
            if (!imageGroups[sku]) {
                imageGroups[sku] = [];
            }
            imageGroups[sku].push(...variantData.images);
        });

        return imageGroups;
    }

    updateMainImages(images) {
        // Update desktop grid
        if (this.dom.productMainImages) {
            this.dom.productMainImages.innerHTML = '';

            const variant = this.getCurrentVariant();
            const hasDiscount = variant && variant.compare_at_price && variant.compare_at_price > variant.price;

            images.forEach((image, index) => {
                if (index < 4) {
                    const imageWrapper = document.createElement('div');
                    imageWrapper.style.width = '100%';
                    imageWrapper.style.height = '100%';
                    imageWrapper.style.cursor = 'pointer';
                    imageWrapper.style.position = 'relative';

                    if (this.dom.imageModal) {
                        imageWrapper.onclick = () => {
                            this.openModalAtSlide(index);
                        };
                    }

                    const imageElement = document.createElement('img');
                    imageElement.src = image.src;
                    imageElement.alt = image.alt;
                    imageElement.width = image.width;
                    imageElement.height = image.height;
                    imageElement.style.width = '100%';
                    imageElement.style.height = '100%';
                    imageElement.style.objectFit = 'cover';
                    imageElement.className = 'product-grid-image-clickable';

                    imageWrapper.appendChild(imageElement);

                    // Add discount badge to first image only
                    if (index === 0 && hasDiscount) {
                        const discountPercentage = Math.round(
                            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100,
                        );

                        const discountBadge = document.createElement('div');
                        discountBadge.className = 'product-discount-percentage';
                        discountBadge.innerHTML = `<span class="discount-price">-${discountPercentage}%</span>`;

                        imageWrapper.appendChild(discountBadge);
                    }

                    this.dom.productMainImages.appendChild(imageWrapper);
                }
            });
        }

        // Update mobile swiper
        if (this.dom.productMainImagesMobile) {
            this.updateMobileSwiper(images);
        }
    }

    updateMobileSwiper(images) {
        if (!this.dom.productMainImagesMobile) return;

        const variant = this.getCurrentVariant();
        const hasDiscount = variant && variant.compare_at_price && variant.compare_at_price > variant.price;

        // Clear existing slides
        this.dom.productMainImagesMobile.innerHTML = '';

        // Add new slides
        images.forEach((image, index) => {
            const slide = document.createElement('swiper-slide');
            slide.style.position = 'relative';

            const imageElement = document.createElement('img');
            imageElement.src = image.src;
            imageElement.alt = image.alt;
            imageElement.width = image.width;
            imageElement.height = image.height;
            imageElement.style.width = '100%';
            imageElement.style.height = '100%';
            imageElement.style.objectFit = 'cover';
            imageElement.style.cursor = 'pointer';
            imageElement.className = 'product-grid-image-clickable';

            // Add click handler to open modal at this slide
            imageElement.onclick = () => {
                this.openModalAtSlide(index);
            };

            slide.appendChild(imageElement);

            // Add discount badge to first slide only
            if (index === 0 && hasDiscount) {
                const discountPercentage = Math.round(
                    ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100,
                );

                const discountBadge = document.createElement('div');
                discountBadge.className = 'product-discount-percentage';
                discountBadge.innerHTML = `<span class="discount-price">-${discountPercentage}%</span>`;

                slide.appendChild(discountBadge);
            }

            this.dom.productMainImagesMobile.appendChild(slide);
        });

        // Reinitialize swiper to update with new slides
        if (this.dom.productMainImagesMobile.swiper) {
            this.dom.productMainImagesMobile.swiper.update();
            this.dom.productMainImagesMobile.swiper.slideTo(0, 0);
        }
    }

    openModalAtSlide(slideIndex) {
        if (!this.dom.imageModal) return;

        // Store the target slide index for later use
        this.targetSlideIndex = slideIndex;

        // Hide modal content initially to prevent flash of first image
        const modalContent = this.dom.imageModal.querySelector('.image-modal-content');
        if (modalContent) {
            modalContent.style.transition = 'none';
            modalContent.style.opacity = '0';
        }

        // Open the modal
        this.dom.imageModal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // Navigate to the clicked image slide immediately
        setTimeout(() => {
            if (this.dom.modalSwiper && this.dom.modalSwiper.swiper) {
                this.dom.modalSwiper.swiper.slideTo(slideIndex, 0);
                this.updateActiveThumbnail(slideIndex);
            }
        }, 100);

        // Override Liquid template's initialization
        setTimeout(() => {
            if (this.dom.modalSwiper && this.dom.modalSwiper.swiper) {
                this.dom.modalSwiper.swiper.slideTo(slideIndex, 0);
                this.updateActiveThumbnail(slideIndex);
            }
        }, 350);

        // Final update and show content
        setTimeout(() => {
            this.updateActiveThumbnail(slideIndex);

            // Show modal content with smooth fade-in
            if (modalContent) {
                modalContent.style.transition = 'opacity 0.3s ease-in-out';
                modalContent.style.opacity = '1';
            }
        }, 450);
    }

    rerenderOptions() {
        if (!this.dom.productDetails) return;

        const data = JSON.parse(this.dom.productDetails.dataset.jsonProduct);
        const { variants } = data;

        const variantId = this.getUrlVariantId() || data.default_variant_id;
        const selectedOptions = document.querySelectorAll('.swatch-element input:checked');
        const selectedOptionsValues = Array.from(selectedOptions).map((option) => option.value);
        const optionsLength = selectedOptionsValues.length;

        if (!selectedOptionsValues.length) return;

        const filteredVariants = variants.filter((variant) => {
            let isMatch = true;
            for (let i = 0; i < optionsLength - 1; i++) {
                if (variant.options[i] !== selectedOptionsValues[i]) {
                    isMatch = false;
                    break;
                }
            }
            return isMatch;
        });

        if (!filteredVariants.length) return;

        // Check if the last option is already selected and valid
        if (optionsLength > 0) {
            const currentLastOption = selectedOptionsValues[optionsLength - 1];
            const isCurrentOptionValid = filteredVariants.some(
                (variant) => variant.options[optionsLength - 1] === currentLastOption,
            );

            // If current selection is valid, don't auto-select another option
            if (isCurrentOptionValid) return;
        }

        const optionToSelect = filteredVariants[0].options[optionsLength - 1];
        if (!optionToSelect) return;

        const escapedValue = optionToSelect.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g, '\\$1');
        const optionElement = document.querySelector(`.swatch-element input[value="${escapedValue}"]`);

        if (optionElement) {
            const optionParent = optionElement.closest('.swatch-element');
        }
    }

    loadVariantData(variantId) {
        const hiddenDataElement = document.querySelector('.hidden-json-data');
        const hiddenProductIdElement = document.querySelector('.hidden-product-id-data');

        if (!hiddenDataElement || !hiddenProductIdElement) {
            console.warn('Hidden data elements not found');
            return;
        }

        const hiddenData = hiddenDataElement.textContent.trim();
        const hiddenProductId = hiddenProductIdElement.textContent.trim();

        if (!hiddenData || !hiddenProductId) {
            console.warn('Hidden data is empty');
            return;
        }

        try {
            const productIds = JSON.parse(hiddenProductId);
            const data = JSON.parse(hiddenData);

            data.forEach((item, idx) => {
                item.product_id = productIds[idx];
            });

            const currentVariantData = data.find((item) => parseInt(item.product_id) === parseInt(variantId));

            if (currentVariantData) {
                this.updateSpecs(currentVariantData);
                this.updateShortDescription(currentVariantData);
                this.updateProductSubtitle(currentVariantData);
                this.updateProductFeatures(currentVariantData);
                this.updateDocumentation(currentVariantData);
            } else {
                console.warn('No variant data found for ID:', variantId, 'Available IDs:', productIds);
            }
        } catch (error) {
            console.error('Error parsing variant data:', error);
        }
    }

    updateShortDescription(data) {
        const shortDescription = document.getElementById('shortDescription');
        if (!shortDescription || !data.short_description) return;

        const description = data.short_description;
        const words = description.split(' ');
        const shortDescriptionText = words.slice(0, 40).join(' ');
        const remainingText = words.slice(40).join(' ');

        shortDescription.innerHTML = `
      ${shortDescriptionText}
      ${remainingText ? `<button id="readMore" onclick="readMore()">... Read More</button>` : ''}
      <span id="more" style="display: none;">${remainingText}</span>
    `;
    }

    updateProductFeatures(data) {
        const featuresContainer = document.getElementById('productFeatures');
        const featuresContainerList = featuresContainer?.querySelector('ul');
        if (featuresContainer && featuresContainerList && data.product_features) {
            const features = data.product_features;

            featuresContainerList.innerHTML = features.map((feature) => `<li>${feature}</li>`).join('');
        }
    }

    updateProductSubtitle(data) {
        const productSubtitle = document.querySelector('.product-type');
        if (productSubtitle && data.product_subtitle) {
            productSubtitle.innerHTML = data.product_subtitle;
        }
    }

    updateSpecs(data) {
        if (!data || !this.dom.specsTable) return;

        const tableContent = Object.entries(data)
            .filter(([key]) => !this.constants.EXCLUDED_SPECS_KEYS.has(key))
            .reduce((acc, [key, value], idx, array) => {
                const mfName =
                    this.constants.SPECS_NAME_MAP.get(key) ||
                    key
                        .split('_')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');

                const mfValue =
                    value === 'true'
                        ? 'Yes'
                        : value === 'false'
                          ? 'No'
                          : typeof value === 'number'
                            ? value.toString().replace(/\.0$/, '')
                            : typeof value === 'string'
                              ? value.replace(/\.0(\s|$)/g, '$1')
                              : value;

                const isLast = idx === array.length - 1;
                const cell = `
          <td>
            <div class="specs-item">
              <p>${mfName}</p>
              <p>${mfValue}</p>
            </div>
          </td>`;

                if (idx % 2 === 0) {
                    return acc + (isLast ? `<tr>${cell}</tr>` : `<tr>${cell}`);
                } else {
                    return acc + `${cell}</tr>`;
                }
            }, '');

        this.dom.specsTable.innerHTML = tableContent;
    }

    updateDocumentation(data) {
        const documentationContainer = document.getElementById('documentation_files_id');
        if (!documentationContainer) return;

        // Get current variant to fetch updated documentation
        const currentVariant = this.getCurrentVariant();
        if (!currentVariant) return;

        // Get product handle from URL
        const pathParts = window.location.pathname.split('/');
        const productHandle = pathParts[pathParts.length - 1];

        // Fetch the product page with the specific variant to get updated documentation
        const url = `/products/${productHandle}?variant=${currentVariant.id}`;

        fetch(url)
            .then((response) => response.text())
            .then((html) => {
                // Parse the response HTML
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // Extract the documentation content from the fetched page
                const newDocContent = doc.querySelector('#documentation_files_id');

                if (newDocContent && newDocContent.innerHTML.trim()) {
                    // Update the documentation container with new content
                    documentationContainer.innerHTML = newDocContent.innerHTML;
                }
            })
            .catch((error) => {
                console.warn('Failed to update documentation for variant:', currentVariant.id, error);
                // Keep existing documentation on error
            });
    }
}

window.readMore = function () {
    const more = document.getElementById('more');
    const readMore = document.getElementById('readMore');
    if (more && readMore) {
        more.style.display = 'inline';
        readMore.style.display = 'none';
    }
};

window.playVideo = function (event) {
    const modal = document.querySelector('.image-modal');
    if (modal && modal.style.display === 'flex') {
        const videoSlide = modal.querySelector('.product-video')?.parentElement;
        if (videoSlide) {
            const thumbnail = videoSlide.querySelector('.image-thumbnail');
            const button = videoSlide.querySelector('button');
            const video = videoSlide.querySelector('.product-video');

            if (thumbnail) thumbnail.style.display = 'none';
            if (button) button.style.display = 'none';
            if (video) {
                video.style.display = 'block';
                video.play();
            }
        }
    }
};

document.addEventListener('DOMContentLoaded', () => {
    new ProductManager();
});
